import { faKey, faUser } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from '@supply-mover-2-0/web-ui';
import { useNavigate } from 'react-router-dom';
import { usePortalAuth } from '../../../hooks/usePortalAuth';
import { clientDisplayId } from '../../../lib/clientDisplayId';

/**
 * Account profile widget — the customer identity header (avatar, name,
 * email, active client + display id) with a desktop "Change Password"
 * action. Extracted verbatim from the hardcoded AccountPage header so it
 * can be placed/positioned via the account-page designer; the account
 * page's fallback layout reuses this same component.
 *
 * Login-required by context (the account page is authenticated). Renders
 * nothing useful when there's no profile, so it returns null then — the
 * grid row collapses, matching the other self-hiding storefront widgets.
 */
export function AccountProfileWidget(): JSX.Element | null {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { profile, activeClient } = usePortalAuth();

	if (profile == null) {
		return null;
	}

	return (
		<div className="bg-brand-surface border border-brand-border rounded-xl p-4">
			<div className="flex items-center gap-3">
				<div className="w-12 h-12 rounded-full bg-portal-accent/20 flex items-center justify-center shrink-0">
					<FontAwesomeIcon icon={faUser} className="text-portal-accent" />
				</div>
				<div className="min-w-0">
					<div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
						{t(profile.firstName)} {t(profile.lastName)}
					</div>
					<div className="text-xs text-brand-muted truncate">
						{t(profile.email)}
					</div>
					{activeClient != null && (
						<div className="text-xs text-brand-muted mt-0.5 truncate">
							{t(activeClient.name)} · {t('ID')}:{' '}
							{t(clientDisplayId(activeClient))}
						</div>
					)}
				</div>
				{/* Desktop account action; mobile surfaces Change Password in the
				    account page's menu list. */}
				<button
					type="button"
					onClick={() => {
						void navigate('/account/password');
					}}
					className="hidden md:inline-flex items-center gap-2 ml-auto px-3 py-2 text-sm text-gray-700 dark:text-gray-200 border border-brand-border rounded-lg hover:bg-brand-input shrink-0"
				>
					<FontAwesomeIcon icon={faKey} className="text-brand-muted" />
					{t('Change Password')}
				</button>
			</div>
		</div>
	);
}
